import { IResourceClient } from '@bom-nextgen-keycloak/models';
import {
  selectPermissionResource,
  selectPermissionResourceDetail,
} from '@bom-nextgen-keycloak/web/core';
import {
  HeadCell,
  OrderTable,
} from '@bom-nextgen-keycloak/web/shared/data-access';
import {
  EnhancedTableHead,
  IconButtonDelete,
  NotFound,
  Pagination,
  StyledTextEllipsis,
  TableContainer,
  TableRowSkeleton,
} from '@bom-nextgen-keycloak/web/shared/ui';
import {
  getComparator,
  stableSort,
} from '@bom-nextgen-keycloak/web/shared/util';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import Paper from '@mui/material/Paper';
import { SelectChangeEvent } from '@mui/material/Select';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import React, { FC } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

type ResourceTableProps = {
  data: IResourceClient[];
  loading?: boolean;
  selectedPage: number;
  totalRecord: number;
  handleChange: (event: SelectChangeEvent<unknown>) => void;
  onClickDelate: (resource: IResourceClient) => void;
};

const headCells: HeadCell<keyof IResourceClient>[] = [
  {
    id: 'name',
    sortKey: 'name',
    numeric: false,
    disablePadding: false,
    label: 'Name',
  },
  {
    id: 'type',
    sortKey: 'type',
    numeric: false,
    disablePadding: false,
    label: 'Type',
  },
  {
    id: 'actions',
    sortKey: '_id',
    numeric: false,
    disablePadding: false,
    label: 'Actions',
  },
];

const ResourceTable: FC<ResourceTableProps> = ({
  data,
  onClickDelate,
  loading,
  selectedPage,
  handleChange,
  totalRecord = 0,
}) => {
  const [order, setOrder] = React.useState<OrderTable>('desc');
  const [orderBy, setOrderBy] = React.useState<keyof IResourceClient>('name');
  const { canViewDelete } = useSelector(selectPermissionResource);
  const { canViewPage } = useSelector(selectPermissionResourceDetail);

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof IResourceClient
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  return (
    <Paper>
      <TableContainer>
        <Table
          aria-label="role table"
          aria-labelledby="tableTitle"
          stickyHeader
        >
          <EnhancedTableHead
            headCells={headCells}
            order={order}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
          />
          <TableBody>
            <TableRowSkeleton loading={loading} cellLength={headCells.length}>
              {data.length ? (
                stableSort<IResourceClient>(
                  data,
                  getComparator(order, orderBy)
                ).map((row) => (
                  <TableRow key={row._id}>
                    <TableCell title={row.name}>
                      <StyledTextEllipsis>{row.name}</StyledTextEllipsis>
                    </TableCell>
                    <TableCell title={row.type}>
                      <StyledTextEllipsis>{row.type}</StyledTextEllipsis>
                    </TableCell>
                    <TableCell>
                      {canViewPage && (
                        <Link to={`/resources/${row._id}`}>
                          <Tooltip title="Edit resource">
                            <IconButtonDelete aria-label="edit">
                              <EditIcon fontSize="small" />
                            </IconButtonDelete>
                          </Tooltip>
                        </Link>
                      )}

                      {canViewDelete && (
                        <Tooltip title="Remove resource">
                          <IconButtonDelete
                            aria-label="delete"
                            onClick={() => onClickDelate(row)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButtonDelete>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6}>
                    <NotFound />
                  </TableCell>
                </TableRow>
              )}
            </TableRowSkeleton>
          </TableBody>
        </Table>
      </TableContainer>
      <Pagination
        selectedPage={selectedPage}
        totalRecord={totalRecord}
        handleChange={handleChange}
      />
    </Paper>
  );
};

export { ResourceTable };
